import classNames from 'classnames';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { getCell } from '@/core/cell';
import { getExplorationTabs, CellDragKey, isCellDragEvent } from '@/core/exploration';
import { TabParameter } from '@/explore/utils/url';

import { SortableItemHorizontal } from '../components/sortable-item';
import { Exploration } from '../types';
import { useExplorationUIContext } from './exploration-ui-context';

import buttonStyles from '@/components/button/button.module.scss';
import styles from './exploration.module.scss';

interface ExplorationTabsProps {
  exploration: Exploration;
  activeTabId: string | null;
  onDropCell: (cellId: string, tabId: string) => void;
  onMoveTab: (fromIndex: number, toIndex: number, after: boolean) => void;
}

export const ExplorationTabs = ({
  exploration,
  activeTabId,
  onDropCell,
  onMoveTab,
}: ExplorationTabsProps) => {
  const [dragOverTab, setDragOverTab] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const { getExplorationLink, setActiveTabId } = useExplorationUIContext();

  const tabs = getExplorationTabs(exploration);

  const handleDrop = (event: React.DragEvent, tabId: string) => {
    if (!isCellDragEvent(event)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    // Get the dragged cell ID from the drag data
    const cellId = event.dataTransfer.getData(CellDragKey);
    const cell = getCell(exploration.view.cells, cellId);

    if (cell !== undefined) {
      onDropCell(cell.id, tabId);
    }

    setDragOverTab(null);
    setIsDragging(false);
  };

  const handleDragOver = (event: React.DragEvent, tabId: string) => {
    if (!isCellDragEvent(event)) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    if (!isDragging) {
      setIsDragging(true);
    }
    if (dragOverTab !== tabId) {
      setDragOverTab(tabId);
    }
  };

  const handleDragEnter = (event: React.DragEvent, tabId: string) => {
    if (!isCellDragEvent(event)) {
      return;
    }
    event.preventDefault();
    setDragOverTab(tabId);
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    const relatedTarget = event.relatedTarget as Node;
    if (tabsContainerRef.current && !tabsContainerRef.current.contains(relatedTarget)) {
      setDragOverTab(null);
      setIsDragging(false);
    }
  };

  return (
    <>
      <div
        ref={tabsContainerRef}
        className={classNames(buttonStyles.buttonGroup, styles.explorationTabs)}
        onDragLeave={handleDragLeave}>
        {tabs.map((tab, index) => {
          const tabUrl = getExplorationLink({ [TabParameter]: tab.tabId });
          return (
            <SortableItemHorizontal
              key={tab.tabId}
              scope="exploration-tabs"
              index={index}
              draggedIndex={draggedIndex}
              setDraggedIndex={setDraggedIndex}
              onReorder={onMoveTab}>
              <Link
                to={tabUrl.href}
                title={tab.label}
                className={classNames(styles.explorationTab, {
                  [buttonStyles.sizeSmall]: true,
                  [buttonStyles.active]: tab.tabId === activeTabId,
                  [styles.dragOver]: dragOverTab === tab.tabId,
                })}
                onDrop={(event) => handleDrop(event, tab.tabId)}
                onDragOver={(event) => handleDragOver(event, tab.tabId)}
                onDragEnter={(event) => handleDragEnter(event, tab.tabId)}
                onClick={(event) => {
                  event.preventDefault();
                  setActiveTabId(tab.tabId, false);
                }}>
                {tab.label}
              </Link>
            </SortableItemHorizontal>
          );
        })}
      </div>
    </>
  );
};
